import { Location } from '@angular/common';
import { Component, DestroyRef, effect, inject, input, numberAttribute, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { provideIcons } from '@ng-icons/core';
import { lucideArrowLeft } from '@ng-icons/lucide';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { HlmCardImports } from '@spartan-ng/helm/card';
import { HlmIconImports } from '@spartan-ng/helm/icon';
import { HlmTooltipImports } from '@spartan-ng/helm/tooltip';
import { debounceTime, distinctUntilChanged } from 'rxjs';

import { Référentiel } from '../../administration/gestion-référentiels/models/référentiel.model';
import { ReferentielStore } from '../../administration/gestion-référentiels/store/referentiel.store';
import { TechniqueAnimationStore } from '../../administration/technique-animation/store/technique-animation.store';
import { Cartographie } from '../../cartographie/models/cartographie.model';
import { ModalitéStore } from '../../modalités/store/modalité.store';
import { InterrelationStore } from '../../référentiel-métier/référentiel-metier-interrelations/store/interrelation.store';
import { AptitudeStore } from '../../référentiel-métier/référentiel-métier-processus/processus-aptitude/store/aptitude.store';

import { Sequence } from '../models/séquence.model';
import { JoinByPipe } from '../pipes/join-by.pipe';
import { ActivitéStore } from '../store/activité.store';
import { ActivitéFormService } from './activité-form.service';
import { AptitudesComponent } from './aptitudes/aptitudes.component';
import { InformationsPratiquesComponent } from './informations-pratiques/informations-pratiques.component';
import { InterrelationsComponent } from './interrelations/interrelations.component';
import { ItemResumeComponent } from './item-resume/item-resume.component';
import { ProductionsComponent } from './productions/productions.component';
import { SupportsComponent } from './supports/supports.component';

@Component({
  selector: 'app-activite-form',
  imports: [
    ReactiveFormsModule,
    HlmIconImports,
    HlmButtonImports,
    HlmCardImports,
    HlmTooltipImports,
    InformationsPratiquesComponent,
    AptitudesComponent,
    InterrelationsComponent,
    ProductionsComponent,
    SupportsComponent,
    ItemResumeComponent,
    RouterModule,
    JoinByPipe,
  ],
  templateUrl: './activite-form.component.html',
  providers: [provideIcons({ lucideArrowLeft })],
})
export class ActiviteFormComponent implements OnInit {
  readonly #aptitudeStore = inject(AptitudeStore);
  readonly #referentielStore = inject(ReferentielStore);
  readonly #interrelationStore = inject(InterrelationStore);
  readonly #destroy = inject(DestroyRef);
  readonly #activitéStore = inject(ActivitéStore);
  readonly #techniqueAnimationStore = inject(TechniqueAnimationStore);
  readonly #modaliteStore = inject(ModalitéStore);
  readonly formService = inject(ActivitéFormService);
  readonly location = inject(Location);

  readonly cartographie = input.required<Cartographie>();
  readonly referentiel = input.required<Référentiel>();
  readonly sequence = input.required<Sequence>();
  readonly idActivite = input<string>();
  readonly etape = input<number, number | string>(0, { alias: 'i', transform: (value) => numberAttribute(value, 0) });

  form = this.formService.form;

  constructor() {
    this.#techniqueAnimationStore.charger();

    effect(() => {
      this.#aptitudeStore.charger({ idCartographie: this.cartographie().id });
      this.#referentielStore.chargerParCartographie(this.cartographie().id);
      this.#interrelationStore.chargerToutes(this.cartographie().id);
      this.#modaliteStore.charger(this.cartographie().id);
    });

    effect(() => {
      const idActivité = this.idActivite();

      if (idActivité) {
        this.#activitéStore.select({ id: idActivité });
      }
    });
  }

  ngOnInit(): void {
    this.form.valueChanges
      .pipe(debounceTime(1000), takeUntilDestroyed(this.#destroy), distinctUntilChanged())
      .subscribe(() => this.formService.submit(this.sequence()));
  }

  publierActivite = () => this.formService.publier();
}
